import { logWarning } from '@databases/logs';
import {
  type BlogpostReferenceOrContentId,
  type Content,
  type ContentBody,
  ContentRepresentation,
  ContentStatus,
  type ContentType,
  type PageReferenceOrContentId,
  type ResolvedBlogpostReference,
  type ResolvedBlogpostReferenceOrContentId,
  type ResolvedPageReference,
  type ResolvedPageReferenceOrContentId,
} from '@databases/types/confluence';

import { parseHTML } from '../lib/dom';
import { getThrottledBatchCallback } from '../utils';

import { getAttachments } from './attachment';
import { searchConfluence } from './confluence';
import { getContentProperty } from './contentProperty';
import { retry } from './fetch';
import { fetchConfluenceData, rejectOnError } from './fetch';
import {
  getContentKey,
  isBlogpostReference,
  isPageReference,
  serializeBlogpostReference,
  serializeContentIdReference,
  serializePageReference,
} from './utils';

export const DATA_LINKED_RESOURCE_ID_ATTR = 'data-linked-resource-id';

export async function getContentById<Type extends ContentType>(
  id: string,
  expand: string[] = []
): Promise<Content<Type>> {
  const res = await retry(
    async () => {
      return await fetchConfluenceData(
        `/rest/api/content/${encodeURIComponent(id)}?${new URLSearchParams([
          ['status', ContentStatus.CURRENT],
          ['status', ContentStatus.ARCHIVED],
          ['expand', expand.join(',')],
        ])}`
      ).then(rejectOnError);
    },
    (err) => err._orderlyStatus === 404,
    3
  );

  return await res.json();
}

export async function* searchContent<Type extends ContentType = ContentType>(
  cql: string,
  expand: string[] = [],
  limit = Infinity
): AsyncGenerator<Content<Type>, void, void> {
  for await (const result of searchConfluence<Content>(cql, expand, limit)) {
    yield result.content as Content<Type>;
  }
}

export interface ConvertOptions {
  to: ContentRepresentation;
  spaceKeyContext?: string;
  contentIdContext?: string;
  signal?: AbortSignal;
}

export async function convertContentBody(
  body: ContentBody,
  { to, spaceKeyContext, contentIdContext, signal }: ConvertOptions,
  expand: string[] = []
): Promise<ContentBody> {
  const res = await fetchConfluenceData(
    `/rest/api/contentbody/convert/${encodeURIComponent(to)}?${new URLSearchParams({
      ...(spaceKeyContext ? { spaceKeyContext } : {}),
      ...(contentIdContext ? { contentIdContext } : {}),
      expand: expand.join(','),
    })}`,
    {
      method: 'POST',
      headers: {
        'Content-type': 'application/json',
      },
      body: JSON.stringify(body),
      signal,
    }
  ).then(rejectOnError);
  return await res.json();
}

/**
 * Resolves the view page URLs and page IDs of the pages with the given space keys and titles (case-insensitive).
 * @return Returns a map whose key is the result of getPageKey(pageRef). Only pages that exist will
 *         be present in the map.
 */
export async function resolveContentReferences(
  contentRefs: Array<PageReferenceOrContentId | BlogpostReferenceOrContentId>
): Promise<
  Map<string, ResolvedPageReferenceOrContentId | ResolvedBlogpostReferenceOrContentId>
> {
  if (contentRefs.length === 0) {
    return new Map();
  }

  const res = await convertContentBody(
    {
      representation: ContentRepresentation.STORAGE,
      value: contentRefs
        .map((contentRef) =>
          isBlogpostReference(contentRef)
            ? serializeBlogpostReference(contentRef, true)
            : isPageReference(contentRef)
            ? serializePageReference(contentRef, true)
            : serializeContentIdReference(contentRef, true)
        )
        .join('\n'),
    },
    {
      to: ContentRepresentation.EXPORT_VIEW,
    }
  );

  const links = parseHTML(res.value).children('a');

  const result = new Map<
    string,
    ResolvedPageReferenceOrContentId | ResolvedBlogpostReferenceOrContentId
  >();
  for (let i = 0; i < contentRefs.length; i++) {
    const href = links.eq(i).attr('href');
    const id = links.eq(i).attr(DATA_LINKED_RESOURCE_ID_ATTR);
    const ref = contentRefs[i];
    if (id) {
      result.set('type' in ref ? getContentKey(ref) : ref.contentId, {
        ...contentRefs[i],
        contentId: id,
        url: href!,
      });
    }
  }
  return result;
}

export const resolveContentReference = getThrottledBatchCallback<
  [PageReferenceOrContentId | BlogpostReferenceOrContentId],
  ResolvedPageReference | ResolvedBlogpostReference | undefined
>(async (args) => {
  const refs = args.map((a) => a[0]);
  const result = await resolveContentReferences(refs);
  return refs.map((ref) =>
    result.get('type' in ref ? getContentKey(ref) : ref.contentId)
  ) as Array<ResolvedPageReference | ResolvedBlogpostReference | undefined>;
});

/**
 * Retrieves the absolute cover picture URL of the given content object. If the content doesn't have a cover picture, undefined is returned.
 */
export async function getCoverPicture(
  contentId: string,
  baseUrl: string
): Promise<string | undefined> {
  const contentProperty = await getContentProperty(
    contentId,
    'cover-picture-id-published'
  );
  let attachmentId: string | undefined;
  if (contentProperty) {
    try {
      const parsed = JSON.parse(contentProperty.value);
      if (parsed.id.startsWith('https://')) {
        return parsed.id;
      } else {
        attachmentId = parsed.id;
      }
    } catch (e: any) {
      logWarning('Could not parse cover picture content property', e);
    }
  }

  if (attachmentId) {
    for await (const attachment of getAttachments(contentId)) {
      if (attachment.extensions?.fileId === attachmentId) {
        return `${baseUrl}${attachment._links.download}`;
      }
    }
  }
}
